import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/src/modules/components/Button';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { useCollection } from '@/src/modules/catalog/useCollection';
import { useSelectProduct } from '@/src/modules/catalog/useSelectProduct';
import { useCatalogPricingMode } from '@/src/modules/catalog/useCatalogPricingMode';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { addItem as addCartItem, CartRejectedError } from '@/src/modules/cart/api';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const { data, loading, error, notFound, reload } = useCollection(productId);
  const selectProduct = useSelectProduct();
  const { isCreditMode, isPartnerUser, hasCredits, remainingCredits, walletBalance, balanceFormatted, loading: pricingLoading } = useCatalogPricingMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);

  async function handleAddToCart() {
    if (!data) return;
    // Cart is authenticated-only (a cart belongs to a user), same as "Buy
    // Now" below. Caught in code review: this page is public
    // (/catalog/:productId has no ProtectedRoute wrapper), and calling the
    // Cart API anonymously would 401, trigger authFetch's logout() on a user
    // who was never signed in, and hard-redirect with the original intent
    // silently lost. Guard before ever making the call, matching Buy Now's
    // own level of handling (redirect to /login) rather than inventing a
    // full resume-after-login flow this pass doesn't have scope for.
    if (!user) {
      navigate('/login');
      return;
    }
    setAddingToCart(true);
    setCartError(null);
    try {
      await addCartItem({ collectionCode: data.code });
      navigate('/cart');
    } catch (err) {
      setCartError(err instanceof CartRejectedError ? err.message : 'Couldn’t add this to your cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  }

  // Product not found or inactive — the backend returns an identical 404 for
  // both (architecture §2.3), so there is a single "no longer available"
  // redirect branch, never a distinct "here's the inactive product anyway" state.
  if (notFound) {
    return (
      <Navigate
        to="/catalog"
        replace
        state={{ catalogBanner: 'That product is no longer available.' }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8">
      {loading && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="aspect-[3/4] animate-pulse rounded-[2rem] bg-surface border border-outline-variant/30" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 animate-pulse rounded-lg bg-surface" />
            <div className="h-4 w-full animate-pulse rounded-lg bg-surface" />
            <div className="h-4 w-5/6 animate-pulse rounded-lg bg-surface" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-sm font-medium text-status-failed">{error}</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="aspect-[3/4] overflow-hidden rounded-[2rem] border border-outline-variant/30 bg-surface-low">
            <TemplateCardPreview template={data.template} />
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-on-surface-variant">
                {data.template.occasionType.replace(/_/g, ' ')}
              </p>
              <h1 className="mt-2 text-3xl font-bold text-on-surface">{data.displayName}</h1>
            </div>

            {data.description && (
              <p className="text-base leading-7 text-on-surface-variant">{data.description}</p>
            )}

            {isCreditMode ? (
              <div className="flex flex-col gap-1" data-testid="detail-credit-price">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-primary">
                    ₹{Math.round(data.amountInPaise / 100).toLocaleString('en-IN')}
                  </span>
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                    Wholesale Partner Pricing
                  </span>
                </div>
                <p className="text-xs text-on-surface-variant">
                  {pricingLoading
                    ? `₹${Math.round(data.amountInPaise / 100).toLocaleString('en-IN')} will be deducted from your wallet balance upon publishing.`
                    : `₹${Math.round(data.amountInPaise / 100).toLocaleString('en-IN')} will be deducted from your wallet balance. Your wallet balance: ₹${(walletBalance ?? 0).toLocaleString('en-IN')}.`}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <p className="text-2xl font-bold text-on-surface">
                  {formatPrice(data.amountInPaise, data.currency)}
                </p>
                {hasCredits && (
                  <p className="text-xs text-on-surface-variant">
                    You have {remainingCredits} publish {remainingCredits === 1 ? 'credit' : 'credits'} available.
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Button variant="primary" size="lg" onClick={() => selectProduct(data)}>
                {isCreditMode ? 'Use Template' : 'Select'}
              </Button>
              {isPartnerUser && !pricingLoading && (walletBalance !== undefined ? !hasCredits : remainingCredits === 0) && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/dashboard/partner?tab=credits')}
                >
                  Buy Partner Pack
                </Button>
              )}
              {!isCreditMode && (
                <>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(`/checkout/${data.id}`)}
                  >
                    Buy Now
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={() => void handleAddToCart()}
                    loading={addingToCart}
                    disabled={addingToCart}
                  >
                    Add to Cart
                  </Button>
                </>
              )}
            </div>
            {cartError && (
              <p role="alert" className="text-sm font-medium text-status-failed">
                {cartError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailPage;
